import type { Database } from './database';
import { prepareQuery } from './query';

export interface Note {
  id: string;
  title: string;
  body: string;
  pinned: boolean;
  archived: boolean;
  trashed: boolean;
  // percent
  width: number;
  // a folder rather than a note: its title is its name, its body empty
  isFolder: boolean;
  // the folder the note is in, if any
  folderId: string | null;
  createdAt: string;
  updatedAt: string;
}

// A note as listed: everything but the body, which can run to megabytes and
// is only wanted open. Its first characters stand in for it, enough for the
// line shown when there is no title. `dirty` says it has changes the remote
// doesn't have yet.
export type NoteSummary = Omit<Note, 'body'> & {
  preview: string,
  dirty: boolean,
};

export const PREVIEW_CHARS = 200;

export const previewOf = (body: string) => body.slice(0, PREVIEW_CHARS);

// what pulling needs to know about each row, without the bodies
export interface SyncState {
  id: string;
  remoteSha: string | null;
  dirty: boolean;
}

// What sync needs on top of a note: where the row stands against the remote.
export interface SyncRow extends Note {
  deletedAt: string | null;
  remoteSha: string | null;
  dirty: boolean;
}

type NoteRow = {
  id: string,
  title: string,
  body: string,
  pinned: number,
  archived: number,
  trashed: number,
  width: number,
  isFolder: number,
  folderId: string | null,
  createdAt: string,
  updatedAt: string,
};

type SummaryRow = Omit<NoteRow, 'body'> & { preview: string, dirty: number };

type SyncDbRow = NoteRow & {
  deletedAt: string | null,
  remoteSha: string | null,
  dirty: number,
};

const COLUMNS = `
  id,
  title,
  pinned,
  archived,
  trashed,
  width,
  is_folder AS isFolder,
  folder_id AS folderId,
  created_at AS createdAt,
  updated_at AS updatedAt`;

const NOTE_COLUMNS = `${COLUMNS}, body`;

const flags = (
  { pinned, archived, trashed }: {
    pinned: number,
    archived: number,
    trashed: number,
  },
) => ({
  pinned: pinned === 1,
  archived: archived === 1,
  trashed: trashed === 1,
});

const toNote = (row: NoteRow): Note => ({
  ...row,
  ...flags(row),
  isFolder: row.isFolder === 1,
});

const toSummary = (row: SummaryRow): NoteSummary => ({
  ...row,
  ...flags(row),
  isFolder: row.isFolder === 1,
  dirty: row.dirty === 1,
});

const toSyncRow = (row: SyncDbRow): SyncRow => ({
  ...toNote(row),
  deletedAt: row.deletedAt,
  remoteSha: row.remoteSha,
  dirty: row.dirty === 1,
});

const now = () => new Date().toISOString();

// the title of a copy of a note kept from the remote: "<title>-server-<n>",
// n one past the highest copy among `taken`
export const serverCopyTitle = (title: string, taken: string[]) => {
  const prefix = `${title}-server-`;
  const numbers = taken
    .filter(t => t.startsWith(prefix))
    .map(t => Number(t.slice(prefix.length)))
    .filter(Number.isInteger);
  return `${prefix}${Math.max(0, ...numbers) + 1}`;
};

export type NewNote = Partial<
  Pick<
    Note,
    | 'title'
    | 'body'
    | 'pinned'
    | 'archived'
    | 'trashed'
    | 'width'
    | 'isFolder'
    | 'folderId'
  >
>;

const newNote = (
  {
    title = '',
    body = '',
    pinned = false,
    archived = false,
    trashed = false,
    width = 100,
    isFolder = false,
    folderId = null,
  }: NewNote,
): Note => {
  const at = now();
  return {
    id: crypto.randomUUID(),
    title,
    body,
    pinned,
    archived,
    trashed,
    width,
    isFolder,
    folderId,
    createdAt: at,
    updatedAt: at,
  };
};

export class Repository {
  constructor(private readonly DB: Database) {}

  async listNotes(): Promise<NoteSummary[]> {
    const { results } = await prepareQuery(
      this.DB,
      `SELECT ${COLUMNS}, dirty, substr(body, 1, :chars) AS preview
         FROM notes
        WHERE deleted_at IS NULL
        -- folders last, by name
        ORDER BY is_folder,
                 CASE WHEN is_folder = 1 THEN title COLLATE NOCASE END,
                 pinned DESC, updated_at DESC`,
    ).bind({ chars: PREVIEW_CHARS }).all<SummaryRow>();
    return results.map(toSummary);
  }

  async getNote(id: string): Promise<Note | null> {
    const row = await prepareQuery(
      this.DB,
      `SELECT ${NOTE_COLUMNS} FROM notes WHERE id = :id AND deleted_at IS NULL`,
    ).bind({ id }).first<NoteRow>();
    return row && toNote(row);
  }

  // what the search indexes, for every note listed - folders aren't searched
  async searchables(): Promise<{ id: string, title: string, body: string }[]> {
    const { results } = await prepareQuery(
      this.DB,
      `SELECT id, title, body FROM notes
        WHERE deleted_at IS NULL AND is_folder = 0`,
    ).bind().all<{ id: string, title: string, body: string }>();
    return results;
  }

  // every note with its body, archived and trashed ones too, but no folders:
  // for a backup
  async allNotes(): Promise<Note[]> {
    const { results } = await prepareQuery(
      this.DB,
      `SELECT ${NOTE_COLUMNS} FROM notes
        WHERE deleted_at IS NULL AND is_folder = 0
        ORDER BY created_at`,
    ).bind().all<NoteRow>();
    return results.map(toNote);
  }

  private insert(note: Note) {
    return prepareQuery(
      this.DB,
      `INSERT INTO notes
         (id, title, body, pinned, archived, trashed, width, is_folder,
          folder_id, created_at, updated_at, dirty)
       VALUES (:id, :title, :body, :pinned, :archived, :trashed, :width,
               :isFolder, :folderId, :createdAt, :updatedAt, 1)`,
    ).bind({
      id: note.id,
      title: note.title,
      body: note.body,
      pinned: note.pinned,
      archived: note.archived,
      trashed: note.trashed,
      width: note.width,
      isFolder: note.isFolder,
      folderId: note.folderId,
      createdAt: note.createdAt,
      updatedAt: note.updatedAt,
    });
  }

  async createNote(fields: NewNote = {}): Promise<Note> {
    const note = newNote(fields);
    await this.insert(note).run();
    return note;
  }

  async createFolder(name: string): Promise<Note> {
    return this.createNote({ title: name, isFolder: true });
  }

  // `base` is the text the edit was typed over. When sync has since replaced
  // the row with the remote's version - it is clean, and no longer `base` -
  // that version is kept as a new note, as a conflict on push would keep it,
  // and the edit goes on over it. Tells whether it kept one.
  async updateNote(
    id: string,
    { title, body }: { title: string, body: string },
    base?: { title: string, body: string },
  ): Promise<boolean> {
    const row = base
      ? await prepareQuery(
        this.DB,
        `SELECT title, body, dirty FROM notes
          WHERE id = :id AND deleted_at IS NULL`,
      ).bind({ id }).first<{ title: string, body: string, dirty: number }>()
      : null;
    const replaced = row && base && !row.dirty
      && (row.title !== base.title || row.body !== base.body)
      && (row.title !== title || row.body !== body);
    const at = now();
    const results = await this.DB.batch([
      // Always tried with a base, and decided here: sync may replace the row
      // between the read above and this write. The read only names the copy.
      ...(base
        ? [
          prepareQuery(
            this.DB,
            `INSERT INTO notes
               (id, title, body, pinned, archived, trashed, width, is_folder,
                folder_id, created_at, updated_at, dirty)
             SELECT :copyId, :copyTitle, body, pinned, archived, trashed,
                    width, is_folder, folder_id, :now, :now, 1
               FROM notes
              WHERE id = :id AND dirty = 0 AND deleted_at IS NULL
                AND (title IS NOT :baseTitle OR body IS NOT :baseBody)
                AND (title IS NOT :title OR body IS NOT :body)`,
          ).bind({
            id,
            title,
            body,
            copyId: crypto.randomUUID(),
            copyTitle: serverCopyTitle(
              row?.title || title || 'untitled',
              replaced ? (await this.listNotes()).map(n => n.title) : [],
            ),
            baseTitle: base.title,
            baseBody: base.body,
            now: at,
          }),
        ]
        : []),
      prepareQuery(
        this.DB,
        `UPDATE notes
            SET title = :title, body = :body, updated_at = :now, dirty = 1
          WHERE id = :id AND (title IS NOT :title OR body IS NOT :body)`,
      ).bind({ id, title, body, now: at }),
    ]);
    return results.length > 1 && results[0].meta.changes > 0;
  }

  async setPinned(id: string, pinned: boolean) {
    await prepareQuery(
      this.DB,
      `UPDATE notes SET pinned = :pinned, updated_at = :now, dirty = 1
        WHERE id = :id`,
    ).bind({ id, pinned, now: now() }).run();
  }

  async setArchived(id: string, archived: boolean) {
    await prepareQuery(
      this.DB,
      `UPDATE notes SET archived = :archived, updated_at = :now, dirty = 1
        WHERE id = :id`,
    ).bind({ id, archived, now: now() }).run();
  }

  // to the trash and back; deleteNote is for good
  async setTrashed(id: string, trashed: boolean) {
    await prepareQuery(
      this.DB,
      `UPDATE notes SET trashed = :trashed, updated_at = :now, dirty = 1
        WHERE id = :id`,
    ).bind({ id, trashed, now: now() }).run();
  }

  // into a folder, or out of any with null
  async setFolder(id: string, folderId: string | null) {
    await prepareQuery(
      this.DB,
      `UPDATE notes SET folder_id = :folderId, updated_at = :now, dirty = 1
        WHERE id = :id AND folder_id IS NOT :folderId`,
    ).bind({ id, folderId, now: now() }).run();
  }

  async setWidth(id: string, width: number) {
    await prepareQuery(
      this.DB,
      `UPDATE notes SET width = :width, updated_at = :now, dirty = 1
        WHERE id = :id AND width IS NOT :width`,
    ).bind({ id, width, now: now() }).run();
  }

  // A note the remote never saw just goes; one it did stays as a tombstone
  // until sync has deleted the remote file.
  async deleteNote(id: string) {
    await this.DB.batch([
      prepareQuery(
        this.DB,
        `DELETE FROM notes WHERE id = :id AND remote_sha IS NULL`,
      ).bind({ id }),
      prepareQuery(
        this.DB,
        `UPDATE notes SET deleted_at = :now, dirty = 1 WHERE id = :id`,
      ).bind({ id, now: now() }),
    ]);
  }

  // For a remote that has none of these notes - another repo, or one wiped:
  // every note goes to it as new on the next push, and deletions it can't
  // have are dropped.
  async forgetRemote() {
    await this.DB.batch([
      prepareQuery(
        this.DB,
        `DELETE FROM notes WHERE deleted_at IS NOT NULL`,
      ).bind(),
      prepareQuery(
        this.DB,
        `UPDATE notes SET remote_sha = NULL, dirty = 1`,
      ).bind(),
    ]);
  }

  async syncRows(): Promise<SyncRow[]> {
    const { results } = await prepareQuery(
      this.DB,
      `SELECT ${NOTE_COLUMNS},
              deleted_at AS deletedAt,
              remote_sha AS remoteSha,
              dirty
         FROM notes`,
    ).bind().all<SyncDbRow>();
    return results.map(toSyncRow);
  }

  // the rows with changes to push
  async dirtyRows(): Promise<SyncRow[]> {
    const { results } = await prepareQuery(
      this.DB,
      `SELECT ${NOTE_COLUMNS},
              deleted_at AS deletedAt,
              remote_sha AS remoteSha,
              dirty
         FROM notes
        WHERE dirty = 1`,
    ).bind().all<SyncDbRow>();
    return results.map(toSyncRow);
  }

  async syncStates(): Promise<SyncState[]> {
    const { results } = await prepareQuery(
      this.DB,
      `SELECT id, remote_sha AS remoteSha, dirty FROM notes`,
    ).bind().all<{ id: string, remoteSha: string | null, dirty: number }>();
    return results.map(r => ({ ...r, dirty: r.dirty === 1 }));
  }

  // Only clears `dirty` if the row is still the version that was pushed: an
  // edit made while the request was in flight has to go out next time, and so
  // does a deletion for good, which leaves `updated_at` as it was. A row gone
  // altogether was deleted for good before it had ever been pushed: it comes
  // back as a tombstone, so the file just written is deleted too.
  async markPushed(id: string, sha: string, pushedUpdatedAt: string) {
    await prepareQuery(
      this.DB,
      `INSERT INTO notes
         (id, created_at, updated_at, deleted_at, remote_sha, dirty)
       VALUES (:id, :now, :now, :now, :sha, 1)
       ON CONFLICT (id) DO UPDATE SET
         remote_sha = excluded.remote_sha,
         dirty = CASE
           WHEN notes.updated_at = :pushedUpdatedAt
            AND notes.deleted_at IS NULL THEN 0
           ELSE 1
         END`,
    ).bind({ id, sha, pushedUpdatedAt, now: now() }).run();
  }

  // The row goes on over `sha` - null when the remote file is gone - and stays
  // dirty. The remote's version, when there is one to keep, becomes a new
  // note in the same transaction, so a retry can't make it twice.
  async rebase(id: string, sha: string | null, copy: NewNote | null) {
    await this.DB.batch([
      ...(copy ? [this.insert(newNote(copy))] : []),
      prepareQuery(
        this.DB,
        `UPDATE notes SET remote_sha = :sha, dirty = 1 WHERE id = :id`,
      ).bind({ id, sha }),
    ]);
  }

  // The remote's version takes the row, unless it changed here since the pull
  // began: that row goes on to conflict on its next push instead. `force` is
  // for a conflict already decided in the remote's favour. Tells whether it
  // did take the row.
  async saveFromRemote(
    note: Note,
    sha: string,
    force = false,
  ): Promise<boolean> {
    const { meta } = await prepareQuery(
      this.DB,
      `INSERT INTO notes
         (id, title, body, pinned, archived, trashed, width, is_folder,
          folder_id, created_at, updated_at, remote_sha, dirty)
       VALUES (:id, :title, :body, :pinned, :archived, :trashed, :width,
               :isFolder, :folderId, :createdAt, :updatedAt, :sha, 0)
       ON CONFLICT (id) DO UPDATE SET
         title = excluded.title,
         body = excluded.body,
         pinned = excluded.pinned,
         archived = excluded.archived,
         trashed = excluded.trashed,
         width = excluded.width,
         is_folder = excluded.is_folder,
         folder_id = excluded.folder_id,
         created_at = excluded.created_at,
         updated_at = excluded.updated_at,
         deleted_at = NULL,
         remote_sha = excluded.remote_sha,
         dirty = 0
       WHERE :force = 1 OR notes.dirty = 0`,
    ).bind({
      id: note.id,
      title: note.title,
      body: note.body,
      pinned: note.pinned,
      archived: note.archived,
      trashed: note.trashed,
      width: note.width,
      isFolder: note.isFolder,
      folderId: note.folderId,
      createdAt: note.createdAt,
      updatedAt: note.updatedAt,
      sha,
      force,
    }).run();
    return meta.changes > 0;
  }

  async forget(id: string) {
    await prepareQuery(this.DB, `DELETE FROM notes WHERE id = :id`)
      .bind({ id }).run();
  }

  // a row that changed here since the pull began stays, to be pushed back
  async forgetIfClean(id: string): Promise<boolean> {
    const { meta } = await prepareQuery(
      this.DB,
      `DELETE FROM notes WHERE id = :id AND dirty = 0`,
    ).bind({ id }).run();
    return meta.changes > 0;
  }

  async getSetting(key: string): Promise<string | null> {
    const row = await prepareQuery(
      this.DB,
      `SELECT value FROM settings WHERE key = :key`,
    ).bind({ key }).first<{ value: string }>();
    return row?.value ?? null;
  }

  async deleteSetting(key: string) {
    await prepareQuery(this.DB, `DELETE FROM settings WHERE key = :key`)
      .bind({ key }).run();
  }

  async setSetting(key: string, value: string) {
    await prepareQuery(
      this.DB,
      `INSERT INTO settings (key, value) VALUES (:key, :value)
       ON CONFLICT (key) DO UPDATE SET value = excluded.value`,
    ).bind({ key, value }).run();
  }
}
